<template>
  <div class="g-tbl">
    <div class="g-tbl__timer">
      <avl-timer only-seconds :timer-amount="getRenewAfter * 1000 || 0"></avl-timer>

      <div class="g-tbl__anonymous-votes" v-if="teamVotes">
        <avl-team-vote
          class="g-tbl__team-vote"
          v-for="(vote, index) in teamVotes"
          :key="index"
          :type="vote"
        />
      </div>
    </div>

    <avl-game-table-seats>
      <avl-game-table-seat
        v-for="(player, index) in getPlayers"
        :key="player.id"
        :total-players="getPlayers.length"
        :number="index"
      >
        <avl-player
          :player="player"
          :roles-are-revealed="rolesAreRevealed"
          :is-selected-for-swapping="player.id === swappedPlayerId"
          @toggleSwapSelection="onToggleSwapSelection($event)"
          @toggleRolesAreRevealed="rolesAreRevealed = !rolesAreRevealed"
        ></avl-player>
      </avl-game-table-seat>
    </avl-game-table-seats>

    <div class="g-tbl__inner" v-if="gameIsStarted || gameIsFinished">
      <div class="g-tbl__inner-wrapper">
        <div class="g-tbl__quests">
          <avl-quest
            class="g-tbl__quest"
            v-for="quest in getQuests"
            :key="quest.number"
            :quest="quest"
          ></avl-quest>
        </div>

        <div class="g-tbl__team-tracker-wrapper">
          <avl-team-tracker class="g-tbl__team-tracker"></avl-team-tracker>

          <h1 class="g-tbl__game-status">Status: {{ getGameStatus }}</h1>

          <div class="g-tbl__teams">
            <span class="g-tbl__team g-tbl__team--servants">{{ getServantsCount || 0 }}</span>

            <span class="g-tbl__separator">vs</span>

            <span class="g-tbl__team g-tbl__team--minions">{{ getMinionsCount || 0 }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="g-tbl__inner" v-else>
      <div class="g-tbl__buttons">
        <div class="g-tbl__button-wrapper">
          <el-button
            class="g-tbl__button"
            :disabled="getPlayers.length === 10 || !!getMyself || getGameIsLocked"
            type="success"
            @click.native="$store.dispatch('lobby/sitDown', $route.params.id)"
          >
            Sit Down
          </el-button>
        </div>

        <div class="g-tbl__button-wrapper">
          <el-button
            class="g-tbl__button"
            :disabled="!getMyself || getGameIsLocked"
            type="danger"
            @click.native="$store.dispatch('lobby/standUp', $route.params.id)"
          >
            Stand Up
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import Timer from './timer';
  import Player from './player';
  import Quest from './quest';
  import TeamTracker from './team-tracker';
  import GameTableSeats from './game-table-seats';
  import GameTableSeat from './game-table-seat';

  export default {
    data: () => ({
      swappedPlayerId: null,
      rolesAreRevealed: false,
    }),
    computed: {
      ...mapGetters('auth', ['user']),
      ...mapGetters([
        'getStartingOptions',
        'getPlayers',
        'getMinionsCount',
        'getServantsCount',
        'getRenewAfter',
        'getActiveQuest',
        'getQuests',
        'getGameIsLocked',
        'gameIsCreated',
        'getGamesRejectedStreak',
        'getGameStatus',
        'gameIsStarted',
        'gameIsFinished',
        'getMyself',
      ]),
      teamVotes() {
        if (!this.getStartingOptions.anonymousVotesMode) return null;

        if (!this.getActiveQuest) return null;

        const { approvesCount, rejectsCount } = this.getActiveQuest.teamProposal;

        if (!approvesCount && !rejectsCount) return null;

        return [...Array(approvesCount).fill(true), ...Array(rejectsCount).fill(false)];
      },
    },
    methods: {
      onToggleSwapSelection(swappedPlayerId) {
        // if this player is already selected,
        // remove the selection from him
        if (this.swappedPlayerId === swappedPlayerId) {
          this.swappedPlayerId = null;

          return;
        }

        // if some other player is selected - swap them
        if (this.swappedPlayerId) {
          this.$store.dispatch('game/swapPlayers', {
            gameId: this.$route.params.id,
            player1: this.swappedPlayerId,
            player2: swappedPlayerId,
          });

          this.swappedPlayerId = null;

          return;
        }

        // if no player is selected, select this one
        this.swappedPlayerId = swappedPlayerId;
      },
    },
    components: {
      'avl-timer': Timer,
      'avl-player': Player,
      'avl-quest': Quest,
      'avl-team-tracker': TeamTracker,
      'avl-game-table-seats': GameTableSeats,
      'avl-game-table-seat': GameTableSeat,
    },
  };
</script>

<style scoped lang="scss">
  $team-vote-width: 20px;
  $team-vote-margin: 2px;
  $team-votes-container-width: 5 * ($team-vote-width + 2 * $team-vote-margin);

  .g-tbl {
    display: inline-block;
    width: 460px;
    height: 460px;
    border-radius: 50%;
    background: #d1d1d1;
    background: url(../../assets/img/game/table.png) no-repeat center;
    background-size: contain;
    //
    position: absolute;
    top: 0;
    left: 50%;
    //
    transform-origin: top left;
    transform: scale(0.5) translateX(-50%);
    @include phone-lg() {
      transform: scale(0.75) translateX(-50%);
    }
    @include tablet-sm() {
      transform-origin: initial;
      transform: translateX(-50%);
    }

    * {
      user-select: none;
    }

    &__timer {
      position: absolute;
      top: 25%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &__anonymous-votes {
      display: flex;
      width: $team-votes-container-width;
      flex-wrap: wrap;
    }

    &__team-vote {
      width: $team-vote-width;
      margin: $team-vote-margin;
    }

    &__inner {
      width: 100%;
      text-align: center;
      //
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &__inner-wrapper {
      padding-top: 64px;
      display: inline-block;
    }

    &__quests {
      width: 100%;
      margin-bottom: 72px;
    }

    &__quest {
      margin: 2px;
    }

    &__team-tracker-wrapper {
      display: inline-block;
      position: relative;
    }

    &__team-tracker {
      display: block;
      text-align: center;
    }

    &__game-status {
      color: #ffffff;
      font-size: 14px;
      font-weight: 400;
      text-align: left;
      margin: 0 0 0 4px;
      //
      position: absolute;
      bottom: -16px;
      left: 0;
      transform: translateY(50%);
    }

    &__teams {
      white-space: nowrap;
      font-size: 14px;
      padding-right: 4px;
      text-align: right;
      //
      position: absolute;
      bottom: -16px;
      right: 0;
      transform: translateY(50%);
    }

    &__team,
    &__separator {
      display: inline-block;
      vertical-align: middle;
    }

    &__team {
      font-size: 20px;

      &--minions {
        color: $red;
      }

      &--servants {
        color: $green;
      }
    }

    &__separator {
      color: #ffffff;
      margin: 0 8px;
    }

    &__buttons {
      text-align: center;
    }

    &__button-wrapper {
      display: block;
      margin-bottom: 16px;
    }
  }
</style>
